// Deals + claimable coupons API client (web). Talks to the shared Django
// endpoints the mobile app uses: GET /api/deals/live/, GET /api/coupons/,
// POST /api/coupons/<id>/claim/. Response shapes mirror views_deals.py /
// views_coupons.py exactly.

import axios from 'axios';
import API_BASE_URL from '../config';

const BASE_URL = `${API_BASE_URL}/api`;

const authHeaders = () => {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) { window.location.href = '/login'; throw new Error('No access token'); }
  return { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' };
};

const on401 = (err) => {
  if (err.response?.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
  }
};

// GET /api/deals/live/ → flattened deal objects (carrying store + valid_until).
export const fetchLiveDeals = async () => {
  try {
    const res = await axios.get(`${BASE_URL}/deals/live/`, { headers: authHeaders() });
    const pages = res.data.flyer_pages || [];
    return pages.flatMap((p) =>
      (p.deals || []).map((d) => ({
        ...d,
        store: d.store || p.store,
        valid_until: p.valid_until,
        flyer_title: p.title,
      })),
    );
  } catch (err) {
    console.error('❌ Error fetching live deals:', err.response?.data || err.message);
    on401(err);
    throw err;
  }
};

// GET /api/coupons/ → coupon[]
export const listCoupons = async (store) => {
  try {
    const url = store ? `${BASE_URL}/coupons/?store=${encodeURIComponent(store)}` : `${BASE_URL}/coupons/`;
    const res = await axios.get(url, { headers: authHeaders() });
    return res.data.coupons || [];
  } catch (err) {
    console.error('❌ Error fetching coupons:', err.response?.data || err.message);
    on401(err);
    throw err;
  }
};

// POST /api/coupons/<id>/claim/ → { wallet_coupon_id, redemption_code, ... }
export const claimCoupon = async (couponId) => {
  try {
    const res = await axios.post(`${BASE_URL}/coupons/${couponId}/claim/`, {}, { headers: authHeaders() });
    return res.data;
  } catch (err) {
    on401(err);
    throw err;
  }
};

// ── deal badge logic (mirrors mobile DealsScreen) ──────────────────────────
export const parsePrice = (s) => {
  if (s == null) return NaN;
  const n = parseFloat(String(s).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : NaN;
};

const roundDownClean = (v) => {
  if (v >= 500) return Math.floor(v / 500) * 500;
  if (v >= 100) return Math.floor(v / 100) * 100;
  if (v >= 50) return Math.floor(v / 50) * 50;
  return Math.floor(v / 10) * 10;
};

// Returns { label, tone } | null. tone: 'pct' | 'type' | 'est'
export const dealBadge = (deal) => {
  const sale = parsePrice(deal.price);
  const orig = parsePrice(deal.original_price);
  if (Number.isFinite(orig) && Number.isFinite(sale) && orig > sale && orig > 0) {
    return { label: `-${Math.round(((orig - sale) / orig) * 100)}% OFF`, tone: 'pct' };
  }
  if (deal.deal_type && String(deal.deal_type).trim()) {
    return { label: String(deal.deal_type).toUpperCase(), tone: 'type' };
  }
  if (Number.isFinite(sale) && sale >= 100) {
    const rate = sale >= 2000 ? 0.4 : sale >= 500 ? 0.65 : 0.3;
    const est = roundDownClean(sale * rate);
    if (est >= 50) return { label: `SAVE OVER $${est}`, tone: 'est' };
  }
  return null;
};
